// Matches ${name} or ${name:-default}
const PARAM_REGEX = /\$\{([a-zA-Z_][a-zA-Z0-9_]*)(?::-([^}]*))?\}/g;
const MAX_PARAMS = 20;

// Escape special characters that could be used for query injection.
// Server-side validation in OpticonService provides the primary protection,
// but this adds defense-in-depth for URL parameter values.
function sanitizeParameterValue(value) {
  if (value === null || value === undefined) {
    return value;
  }
  const stringValue = String(value);
  return stringValue
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/'/g, "\\'");
}

function getNonEmptyUrlParam(urlParams, name) {
  const value = urlParams.get(name);
  if (value === null || value === "") {
    return null;
  }
  return value;
}

function normalizeQueries(queries) {
  if (Array.isArray(queries)) {
    return queries;
  }
  return [queries || ""];
}

// Extract parameters with their inline defaults
// Returns: [{ name: "hostname", default: "prod-1" }, { name: "env", default: null }]
export function extractParameters(query) {
  const params = new Map();
  let match;
  const regex = new RegExp(PARAM_REGEX.source, "g");
  while ((match = regex.exec(query)) !== null) {
    const [, name, defaultValue] = match;
    if (!params.has(name)) {
      params.set(name, { name, default: defaultValue ?? null });
    }
  }
  return Array.from(params.values()).slice(0, MAX_PARAMS);
}

// Substitute ${param} or ${param:-default} with URL param or inline default
export function resolveParameters(query) {
  const urlParams = new URLSearchParams(window.location.search);
  return query.replace(PARAM_REGEX, (match, paramName, defaultValue) => {
    const urlValue = getNonEmptyUrlParam(urlParams, paramName);
    if (urlValue !== null) {
      // Sanitize URL parameter values (user-controlled input)
      return sanitizeParameterValue(urlValue);
    }
    // Inline defaults are authored by dashboard creators and don't need sanitization
    return defaultValue ?? match;
  });
}

// Check if query has unresolved parameters (no URL param AND no inline default)
export function hasUnresolvedParameters(query) {
  const urlParams = new URLSearchParams(window.location.search);
  const params = extractParameters(query);
  return params.some((p) => getNonEmptyUrlParam(urlParams, p.name) === null && p.default === null);
}

// Get current values for all parameters (from URL or defaults)
// Accepts a single query string or an array of queries.
export function getParameterValues(queries) {
  const urlParams = new URLSearchParams(window.location.search);
  const normalizedQueries = normalizeQueries(queries);
  const params = new Map();

  normalizedQueries.forEach((query) => {
    extractParameters(query).forEach((param) => {
      if (!params.has(param.name)) {
        if (params.size >= MAX_PARAMS) {
          return;
        }
        params.set(param.name, {
          name: param.name,
          defaults: new Set(),
          hasRequired: false,
        });
      }

      const entry = params.get(param.name);
      if (param.default === null) {
        entry.hasRequired = true;
      } else {
        entry.defaults.add(param.default);
      }
    });
  });

  return Array.from(params.values()).map((entry) => {
    const urlValue = getNonEmptyUrlParam(urlParams, entry.name);
    const defaultValues = Array.from(entry.defaults);
    let defaultState = "required";
    let consistentDefault = null;

    if (!entry.hasRequired && defaultValues.length === 1) {
      defaultState = "consistent";
      consistentDefault = defaultValues[0];
    } else if (defaultValues.length > 0) {
      defaultState = "varies";
    }

    return {
      name: entry.name,
      value: urlValue ?? (defaultState === "consistent" ? consistentDefault : ""),
      defaultState,
      hasDefault: defaultState === "consistent",
    };
  });
}
